/**
 * Клиент API багфикс-агента (/api/bugfix-agent/…): токен пользователя — из lib/auth.
 */
import { getTokens } from "@/lib/auth"
import type { Build, BuildsOverview } from "../shared/builds"
import type { Attachment, BugfixConfigStatus, BugfixTask, TaskCard, TaskStatus } from "../shared/types"
import { uploadViaV3 } from "./v3-upload"

export class BugfixApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
  }
}

function token(): string {
  const t = getTokens()?.accessToken
  if (!t) throw new BugfixApiError(401, "Нет авторизации — войдите заново")
  return t
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/bugfix-agent${path}`, {
    ...init,
    cache: "no-store",
    headers: { Authorization: `Bearer ${token()}`, ...(init.body ? { "Content-Type": "application/json" } : {}), ...init.headers },
  })
  const body = (await res.json().catch(() => ({}))) as { error?: string }
  if (!res.ok) throw new BugfixApiError(res.status, body.error || `Ошибка сервера (HTTP ${res.status})`)
  return body as T
}

const post = (body?: unknown): RequestInit => ({ method: "POST", body: JSON.stringify(body ?? {}) })

/** Файл → API v3 из браузера, затем вложение задачи (см. v3-upload.ts) */
export function uploadAttachment(file: File, onProgress?: (fraction: number) => void, signal?: AbortSignal): Promise<Attachment> {
  return uploadViaV3("/api/bugfix-agent/uploads", file, onProgress, signal)
}

export const bugfixApi = {
  config: () => request<BugfixConfigStatus>("/config"),
  list: (filter: { status?: TaskStatus[]; author?: number[] } = {}) => {
    const q = new URLSearchParams()
    if (filter.status?.length) q.set("status", filter.status.join(","))
    if (filter.author?.length) q.set("author", filter.author.join(","))
    const qs = q.toString()
    return request<{ tasks: TaskCard[] }>(`/tasks${qs ? `?${qs}` : ""}`).then((r) => r.tasks)
  },
  get: (id: string) => request<{ task: BugfixTask; card: TaskCard }>(`/tasks/${id}`),
  create: (input: { title?: string; description: string; attachmentIds: string[]; workspaceId?: number | null }) => request<{ task: BugfixTask }>("/tasks", post(input)).then((r) => r.task),
  message: (id: string, input: { text: string; attachmentIds: string[] }) => request<{ task: BugfixTask }>(`/tasks/${id}/messages`, post(input)).then((r) => r.task),
  transition: (id: string, input: { to: TaskStatus; comment?: string; attachmentIds?: string[] }) => request<{ task: BugfixTask }>(`/tasks/${id}/transition`, post(input)).then((r) => r.task),
  /** archived: true — в архив из текущей колонки; false — обратно в неё же */
  archive: (id: string, archived: boolean) => request<{ task: BugfixTask }>(`/tasks/${id}/archive`, post({ archived })).then((r) => r.task),
  stop: (id: string) => request<{ task: BugfixTask }>(`/tasks/${id}/stop`, post()).then((r) => r.task),
  retry: (id: string) => request<{ task: BugfixTask }>(`/tasks/${id}/retry`, post()).then((r) => r.task),
  seen: (id: string) => request<{ ok: true }>(`/tasks/${id}/seen`, post()),
  remove: (id: string) => request<{ ok: true }>(`/tasks/${id}`, { method: "DELETE" }),
  builds: () => request<BuildsOverview>("/builds"),
  build: (input: { branch?: string; taskId?: string; mergeBase?: boolean } = {}) => request<{ build: Build }>("/builds", post(input)).then((r) => r.build),
  cancelBuild: (id: string) => request<{ ok: true }>(`/builds/${encodeURIComponent(id)}/cancel`, post()),
  /** Живой лог run: fetch-стрим (EventSource не умеет заголовок Authorization) */
  async stream(id: string, onEvent: (event: string, data: unknown) => void, signal: AbortSignal): Promise<void> {
    const res = await fetch(`/api/bugfix-agent/tasks/${id}/stream`, { headers: { Authorization: `Bearer ${token()}`, Accept: "text/event-stream" }, signal, cache: "no-store" })
    if (!res.ok || !res.body) throw new BugfixApiError(res.status, "Живой лог недоступен")
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buf = ""
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })
      let i: number
      while ((i = buf.indexOf("\n\n")) >= 0) {
        const block = buf.slice(0, i)
        buf = buf.slice(i + 2)
        const event = block.match(/^event:\s*(.+)$/m)?.[1]?.trim() ?? "message"
        const data = block
          .split("\n")
          .filter((l) => l.startsWith("data:"))
          .map((l) => l.slice(5).trimStart())
          .join("\n")
        let parsed: unknown = data
        try {
          parsed = data ? JSON.parse(data) : null
        } catch {
          // текст
        }
        onEvent(event, parsed)
      }
    }
  },
}
